import { simSensorId } from '@/common/utils/simulatorIds';
import type { SensorFormRow } from './sensorFormPayload';

const NAMED_SENSOR_LIMIT = 3;
const TOOLTIP_PRODUCT_LIMIT = 5;

/** Tracking ids of the rows that pick any of these products. */
export function sensorsUsingProducts(
  rows: SensorFormRow[],
  productIds: string[]
): string[] {
  if (productIds.length === 0) return [];
  const removed = new Set(productIds);
  return rows
    .filter((row) => (row.product_ids ?? []).some((id) => removed.has(id)))
    .map((row) => row.sensor_tracking_id ?? '');
}

/** The rows with these products taken out of their picks. */
export function withoutProducts(
  rows: SensorFormRow[],
  productIds: string[]
): SensorFormRow[] {
  const removed = new Set(productIds);
  return rows.map((row) =>
    row.product_ids?.some((id) => removed.has(id))
      ? {
          ...row,
          product_ids: row.product_ids.filter((id) => !removed.has(id)),
        }
      : row
  );
}

/** Products picked on any row that the shipment doesn't carry yet, in pick order. */
export function productsMissingFromShipment(
  rows: SensorFormRow[],
  shipmentProductIds: string[]
): string[] {
  const onShipment = new Set(shipmentProductIds);
  const missing: string[] = [];
  for (const row of rows) {
    for (const id of row.product_ids ?? []) {
      if (!onShipment.has(id)) {
        onShipment.add(id);
        missing.push(id);
      }
    }
  }
  return missing;
}

/** "T-1, T-2, T-3 and 47 more": at most three names, then a count. */
export function formatSensorList(trackingIds: string[]): string {
  const named = trackingIds.slice(0, NAMED_SENSOR_LIMIT).join(', ');
  const rest = trackingIds.length - NAMED_SENSOR_LIMIT;
  return rest > 0 ? `${named} and ${rest} more` : named;
}

/** A row's product summary: the name for one product, a count for several. */
export function productSummary(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.length} products`;
}

/** Tooltip text for a row's products: the first names, then "+N more". */
export function productTooltip(names: string[]): string {
  const shown = names.slice(0, TOOLTIP_PRODUCT_LIMIT).join(', ');
  const rest = names.length - TOOLTIP_PRODUCT_LIMIT;
  return rest > 0 ? `${shown} +${rest} more` : shown;
}

/** Field errors for the sensor card; an absent key means that field is valid. */
export interface SensorDraftErrors {
  sensor?: string;
  sensor_tracking_id?: string;
}

/** Today's sensor row rules, applied when a sensor card is saved. */
export function validateSensorDraft(
  draft: SensorFormRow,
  otherRows: SensorFormRow[],
  isSimulatorSensor: (value: unknown) => boolean,
  isSimSensorId: (value: string) => boolean
): SensorDraftErrors {
  const errors: SensorDraftErrors = {};
  const rawTid = draft.sensor_tracking_id ?? '';
  const tid = rawTid.trim();
  if (!draft.sensor) {
    errors.sensor = 'Please select sensor';
  }
  if (rawTid.length > 0 && tid === '') {
    errors.sensor_tracking_id = 'Only spaces are not allowed';
  } else if (!tid) {
    errors.sensor_tracking_id = 'Please enter sensor tracking ID';
  } else if (isSimulatorSensor(draft.sensor) && !isSimSensorId(tid)) {
    errors.sensor_tracking_id =
      'Simulator sensor IDs look like SIM-YYMMDD-HHMMSS-XXXX-S1';
  } else {
    const normalized = tid.replace(/\s+/g, '').toLowerCase();
    const duplicate = otherRows.some(
      (row) =>
        (row.sensor_tracking_id ?? '').replace(/\s+/g, '').toLowerCase() ===
        normalized
    );
    if (duplicate) {
      errors.sensor_tracking_id = 'Duplicate sensor tracking ID is not allowed';
    }
  }
  return errors;
}

/** The next unused simulator sensor ID on this base, numbered after the simulator sensors already added. */
export function nextSimSensorId(
  rows: SensorFormRow[],
  base: string,
  isSimulatorSensor: (value: unknown) => boolean
): string {
  const taken = new Set(rows.map((row) => row.sensor_tracking_id));
  let index = rows.filter((row) => isSimulatorSensor(row.sensor)).length + 1;
  while (taken.has(simSensorId(base, index))) index += 1;
  return simSensorId(base, index);
}
